// Four short coach marks over the real controls. Skippable from the first tip and
// never blocking for long: each tip is one tap away from finishing.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { track } from "../lib/analytics";

const STEPS = [
  { key: "destination", testid: "destination-pill", title: "Where to?", text: "Search a destination and see parking around it." },
  { key: "layers", testid: "map-layers-btn", title: "Layers", text: "Choose what you see: live, free and paid parking." },
  { key: "share", testid: "share-spot-fab", title: "Share a Spot", text: "Leaving a spot, spotted a free one, or correcting parking info." },
  { key: "car", testid: "save-parking-btn", title: "My Car", text: "Save where you parked and find your car again." },
];

const PAD = 8;

export default function CoachMarks({ open, onDone, replay = false }) {
  const [step, setStep] = React.useState(0);
  const [rect, setRect] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    track(replay ? "tutorial_replayed" : "coach_marks_started", { type: "tips" });
  }, [open, replay]);

  React.useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = document.querySelector(`[data-testid='${STEPS[step].testid}']`);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    const t = setTimeout(measure, 120);
    return () => clearTimeout(t);
  }, [open, step]);

  const finish = (skipped) => {
    track(skipped ? "coach_marks_skipped" : "coach_marks_completed", { step: step + 1 });
    onDone(skipped);
  };

  const next = () => (step === STEPS.length - 1 ? finish(false) : setStep((v) => v + 1));
  const s = STEPS[step];

  // Tooltip sits below the target unless that would leave the screen.
  const below = rect ? rect.top + rect.height + 120 < window.innerHeight : true;
  const tipStyle = rect
    ? below
      ? { top: rect.top + rect.height + 12 }
      : { bottom: window.innerHeight - rect.top + 12 }
    : { top: "45%" };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[3900]"
          data-testid="coach-marks"
          onClick={next}
        >
          {rect ? (
            <div
              className="absolute rounded-2xl"
              style={{
                top: rect.top - PAD,
                left: rect.left - PAD,
                width: rect.width + PAD * 2,
                height: rect.height + PAD * 2,
                boxShadow: "0 0 0 9999px rgba(10,10,12,0.78), 0 0 0 2px #ccff00 inset",
              }}
              aria-hidden="true"
            />
          ) : (
            <div className="absolute inset-0 bg-[#0a0a0c]/80" aria-hidden="true" />
          )}

          <motion.div
            key={s.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute left-3 right-3 sm:left-auto sm:right-auto sm:max-w-xs rounded-2xl bg-[#141416] border border-white/10 p-3.5 shadow-xl"
            style={tipStyle}
            data-testid={`coach-step-${s.key}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#ccff00] font-semibold">
              Tip {step + 1} of {STEPS.length}
            </div>
            <div className="mt-1 font-heading text-base font-semibold text-white">{s.title}</div>
            <p className="mt-1 text-[13px] text-zinc-400 leading-snug">{s.text}</p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={next}
                className="h-9 px-4 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px]"
                data-testid="coach-next"
              >
                {step === STEPS.length - 1 ? "Got it" : "Next"}
              </button>
              <button
                type="button"
                onClick={() => finish(true)}
                className="h-9 px-3 rounded-full border border-white/10 text-zinc-400 text-[13px]"
                data-testid="coach-skip"
              >
                Skip tips
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
